/**
 * Katalog „Co nowego” — skrót zmian w pracy panelu (nie changelog dla programisty).
 * Dialog = niewidziane wpisy; /panel/co-nowego = pełna lista dla roli.
 *
 * Zasady treści → `.cursor/rules/whats-new-release.mdc`
 * (i wspólna reguła użytkownika `whats-new-content`, jeśli obecna).
 */

export type WhatsNewAudience = 'admin' | 'secretary' | 'teacher' | 'student' | 'staff' | 'all';

export type WhatsNewRole = 'administrator' | 'secretary' | 'teacher' | 'student';

export type WhatsNewEntry = {
	version: string;
	audience: WhatsNewAudience;
	title: string;
	bullets: string[];
	/** Relatywny link do pomocy (+ kotwica). Brak pomocy → "". */
	helpHref: string;
};

/**
 * Semver produktu „Co nowego” — utrzymuj zsynchronizowane z root `package.json`.
 * Osobne od znaczników FE:/BE: w meta (deploy stamp).
 */
export const CURRENT_APP_VERSION = '4.7.19';

/** Semver compare: a < b → ujemne. */
export function compareSemver(a: string, b: string): number {
	const pa = a
		.replace(/^v/, '')
		.split('.')
		.map((x) => parseInt(x, 10) || 0);
	const pb = b
		.replace(/^v/, '')
		.split('.')
		.map((x) => parseInt(x, 10) || 0);
	const n = Math.max(pa.length, pb.length);
	for (let i = 0; i < n; i++) {
		const d = (pa[i] ?? 0) - (pb[i] ?? 0);
		if (d !== 0) return d;
	}
	return 0;
}

export function roleToWhatsNewAudience(role: string | null | undefined): WhatsNewRole | null {
	if (role === 'administrator') return 'administrator';
	if (role === 'secretary') return 'secretary';
	if (role === 'teacher') return 'teacher';
	if (role === 'student') return 'student';
	return null;
}

function audienceMatches(entryAudience: WhatsNewAudience, role: WhatsNewRole): boolean {
	if (entryAudience === 'all') return true;
	if (entryAudience === 'staff') {
		return role === 'administrator' || role === 'secretary' || role === 'teacher';
	}
	if (entryAudience === 'admin') return role === 'administrator';
	if (entryAudience === 'secretary') return role === 'secretary';
	if (entryAudience === 'teacher') return role === 'teacher';
	if (entryAudience === 'student') return role === 'student';
	return false;
}

export const WHATS_NEW_ENTRIES: WhatsNewEntry[] = [
	{
		version: '4.7.19',
		audience: 'admin',
		title: 'Rejestracja ucznia z poprzedniej edycji',
		bullets: [
			'Uczeń z kontem z zeszłego roku: po e-mailu i potwierdzeniu hasła wczytywane są dane kontaktowe, adres, szkoła i klasa (+1); nauczycieli i zgody uzupełnia na nowo.',
			'W formularzu rejestracji ucznia kolejność: dane osobowe → Dane dostępu do konta (e-mail) → Adres → szkoła.',
		],
		helpHref: '',
	},
	{
		version: '4.7.19',
		audience: 'secretary',
		title: 'Rejestracja — co mówić uczniom',
		bullets: [
			'Uczeń wracający z poprzedniej edycji może po e-mailu potwierdzić hasło i dostać wczytane dane (w tym szkołę); nauczycieli wybiera na nowo.',
		],
		helpHref: '',
	},
	{
		version: '4.7.19',
		audience: 'teacher',
		title: 'Rejestracja uczniów',
		bullets: [
			'Uczeń z poprzedniej edycji może przy rejestracji potwierdzić hasło i mieć wczytane dane (w tym szkołę) — nauczyciela wybiera ponownie.',
		],
		helpHref: '',
	},
	{
		version: '4.7.18',
		audience: 'teacher',
		title: 'Więcej uczniów na liście',
		bullets: [
			'Przy rejestracji i w danych nauczyciela możesz wpisać więcej uczniów na liście deklarowanych — lista nie ucina się tak szybko jak wcześniej.',
			'Uczeń z orzeczeniem/opinią o dysgrafii może załączyć skan już przy rejestracji (albo później na stronie Konta).',
		],
		helpHref: '',
	},
	{
		version: '4.7.18',
		audience: 'admin',
		title: 'Lista uczniów u nauczyciela i skan opinii',
		bullets: [
			'Nauczyciel może wpisać więcej uczniów na liście deklarowanych (rejestracja i edycja w panelu).',
			'Uczeń z zaznaczonym orzeczeniem/opinią o dysgrafii widzi to na Koncie i może wgrać skan także podczas rejestracji (albo później na Koncie).',
			'Na liście Uczniowie kolumna „Skan” — „Podgląd” otwiera skan (Google Drive); w edycji danych ucznia też jest podgląd.',
			'Skany trafiają do podkatalogu okręgu na Dysku (tworzony automatycznie) — można udostępnić folder sekretarzowi.',
		],
		helpHref: '',
	},
	{
		version: '4.7.18',
		audience: 'secretary',
		title: 'Rejestracja — co mówić szkołom',
		bullets: [
			'Nauczyciel przy rejestracji może podać dłuższy spis deklarowanych uczniów.',
			'Uczeń z orzeczeniem/opinią o dysgrafii może załączyć skan już w formularzu rejestracji albo później na stronie Konta.',
			'Na liście Uczniowie w kolumnie „Skan” jest „Podgląd”, gdy plik jest w systemie.',
		],
		helpHref: '',
	},
	{
		version: '4.7.17',
		audience: 'admin',
		title: 'Szkoły — województwo i powiat z RSPO',
		bullets: [
			'Odświeżenie RSPO liczy „Brak w rejestrze” po województwie/powiecie z rejestru (zapisane przy szkole), nie po kodzie pocztowym.',
			'W Ustawieniach zamiast naprawy po ZIP: „Przywróć okręgi z poprzedniej edycji (RSPO)” — Mazowsze nadal dzieli się na I/II według kodu.',
			'W zakładce Szkoły widać i edytuje się województwo oraz powiat; Szukaj obejmuje też miasto, powiat i województwo.',
		],
		helpHref: '',
	},
	{
		version: '4.7.16',
		audience: 'admin',
		title: 'Rejestracja ucznia — kilku nauczycieli',
		bullets: [
			'W formularzu rejestracji ucznia pole „Nauczyciele” pozwala wskazać do trzech opiekunów — lista nie zamyka się po każdym wyborze.',
			'Można to przekazać szkołom przy rejestracji (wcześniej wiele osób widziało tylko jednego nauczyciela).',
		],
		helpHref: '',
	},
	{
		version: '4.7.16',
		audience: 'secretary',
		title: 'Rejestracja — co mówić uczniom',
		bullets: [
			'Uczestnik podczas rejestracji może wskazać od jednego do trzech nauczycieli (pole „Nauczyciele”); lista wyboru pozostaje otwarta po wybraniu pierwszego.',
		],
		helpHref: '',
	},
	{
		version: '4.7.15',
		audience: 'admin',
		title: 'Odświeżenie szkół z RSPO — mniej fałszywych „Usuń”',
		bullets: [
			'„Brak w rejestrze” / Usuń tylko dla placówek zlikwidowanych lub niedostępnych w API — nie dla szkół z „0 uczniów” w RSPO (częste na starcie roku).',
			'W podsumowaniu widać osobno liczbę do importu (uczniowie > 0) i liczbę placówek obecnych w rejestrze API.',
		],
		helpHref: '',
	},
	{
		version: '4.7.14',
		audience: 'admin',
		title: 'Okręgi mazowieckie — model per wariant',
		bullets: [
			'Duża OLiJP: okręgi Warszawa (kody 00–04, cała Warszawa z prawobrzeżną) i Mazowsze — bez podziału Mazowsze I/II.',
			'OLiJP-SP bez zmian: mazowieckie I (00–02) i mazowieckie II (reszta).',
			'Prefill wracającego nauczyciela na SP dostaje poprawną kategorię (szkoła podstawowa), nie etykietę dużej olimpiady.',
		],
		helpHref: '',
	},
	{
		version: '4.7.13',
		audience: 'admin',
		title: 'Szkoły — okręgi i edycja w panelu',
		bullets: [
			'Naprawa: po synchronizacji RSPO szkoły spoza Mazowsza (np. Radzyń 21-300 → lubelskie) nie trafiają już do mazowieckie II.',
			'Ustawienia: przycisk „Napraw okręgi szkół po kodzie pocztowym” — przywraca województwo z kodu (użyj na test-mala po błędnym przeprasowaniu).',
			'Po przełączeniu „Edycja w panelu” filtr okręgu resetuje się na wszystkie — wcześniej zostawało id z poprzedniej edycji i lista bywała pusta.',
		],
		helpHref: '',
	},
	{
		version: '4.7.12',
		audience: 'admin',
		title: 'Rejestracja — potwierdzenie e-mail',
		bullets: [
			'Po kliknięciu linku z maila: jasny komunikat podziękowania, przycisk Przejdź do logowania i automatyczne przekierowanie po 5 s; ponowne otwarcie tego samego linku nie pokazuje już fałszywego błędu.',
		],
		helpHref: '',
	},
	{
		version: '4.7.11',
		audience: 'admin',
		title: 'Odświeżenie szkół RSPO',
		bullets: [
			'Filtr akcji w tabeli różnic (Pomiń, Dodaj, Zastosuj, Usuń) — łatwiej przejrzeć co zostaje bez zmian.',
			'Podsumowanie „Domyślnie pominięte” liczy wszystkie wiersze z akcją Pomiń (nie tylko ręczne korekty).',
		],
		helpHref: '',
	},
	{
		version: '4.7.10',
		audience: 'admin',
		title: 'Odświeżenie szkół RSPO',
		bullets: [
			'„Brak w RSPO” — domyślnie Pomiń, gdy tę placówkę (ten numer RSPO) wybrał uczeń w poprzedniej lub bieżącej edycji; szkoły muzyczne też domyślnie Pomiń.',
		],
		helpHref: '',
	},
	{
		version: '4.7.9',
		audience: 'admin',
		title: 'Odświeżenie szkół RSPO',
		bullets: [
			'Przy filtrze województwa (np. mazowieckie) lista „Brak w RSPO” nie obejmuje już szkół z innych regionów — zakres liczy się po kodzie pocztowym, nie tylko po okręgu w bazie.',
		],
		helpHref: '',
	},
	{
		version: '4.7.8',
		audience: 'admin',
		title: 'Odświeżenie szkół RSPO',
		bullets: [
			'W dialogu różnic RSPO można ograniczyć listę do województwa (np. lubelskie) — wygodniej niż cała Polska lub pojedyncze powiaty.',
			'Filtr powiatu nadal działa obok województwa, gdy trzeba zawęzić zakres jeszcze bardziej.',
		],
		helpHref: '',
	},
	{
		version: '4.7.7',
		audience: 'admin',
		title: 'Odświeżenie szkół RSPO',
		bullets: [
			'Wiersze „Brak w RSPO” — szkoły w rejestrze bez placówki w imporcie; domyślnie Usuń z rejestru edycji (można Pomiń, zwłaszcza przy ręcznej korekcie).',
			'Różnica samego prefiksu ulicy (ul./os./pl./al.) nie trafia już do listy zmian — widać efektywne różnice.',
		],
		helpHref: '',
	},
	{
		version: '4.7.6',
		audience: 'admin',
		title: 'Odświeżenie szkół RSPO',
		bullets: [
			'Tabela różnic pokazuje pełny adres (ulica, budynek, kod, miasto) po obu stronach; różnice w polach opisane po polsku (np. ulica, budynek).',
			'Gdy różni się tylko nazwa i lokalnie jest dopisek „w” / „we” + miasto (z półautomatycznych dokumentów), domyślnie ustawiane jest Pomiń.',
		],
		helpHref: '',
	},
	{
		version: '4.7.5',
		audience: 'admin',
		title: 'Odświeżenie szkół RSPO',
		bullets: [
			'Filtr powiatu (np. radzyński) wczytuje dane z RSPO tylko dla tego powiatu — znacznie szybciej niż wcześniej.',
			'Pominięte są placówki zlikwidowane i bez uczniów; ponowne wczytanie diff i zastosowanie zmian korzysta z cache (bez drugiego pełnego pobierania).',
		],
		helpHref: '',
	},
	{
		version: '4.7.4',
		audience: 'teacher',
		title: 'Formularz rejestracji',
		bullets: [
			'Przy powrocie z poprzedniej edycji szkoła dopasowuje się po mieście, ulicy i numerze budynku także gdy w bazie jest prefiks „ul.”.',
		],
		helpHref: '',
	},
	{
		version: '4.7.3',
		audience: 'teacher',
		title: 'Status uczniów',
		bullets: [
			'W `Uczniowie` kolumna `Status`: niebieski okrąg — zadeklarowany przez nauczyciela; pomarańczowy + — zarejestrowany (np. przez rodzica), jeszcze nie na liście — kliknięcie dopisuje ucznia; zielony check — zadeklarowany i zarejestrowany.',
		],
		helpHref: '',
	},
	{
		version: '4.7.3',
		audience: 'admin',
		title: 'Potwierdzenie e-mail',
		bullets: [
			'W edycji nauczyciela i ucznia możesz ręcznie zaznaczyć „Adres email potwierdzony”, gdy potwierdzisz poprawność adresu poza linkiem.',
			'Nauczyciele bez potwierdzonego e-maila nie pojawiają się na liście wyboru przy rejestracji ucznia.',
		],
		helpHref: '',
	},
	{
		version: '4.7.1',
		audience: 'admin',
		title: 'Szablony e-mail rejestracji',
		bullets: [
			'Ustawienia motywu → Wiadomości email: osobne szablony dla nowego konta (potwierdzenie adresu), wznowienia na edycję (bez linku aktywacyjnego) i resetu hasła. Placeholdery m.in. {edition}, {school_year}, {login}, {link}.',
		],
		helpHref: '',
	},
	{
		version: '4.7.0',
		audience: 'teacher',
		title: 'Rejestracja na nową edycję z kontem z zeszłego roku',
		bullets: [
			'Po wpisaniu e-maila z poprzedniej edycji należy potwierdzić hasło — wczytamy dane kontaktowe i adres (bez placówki i listy uczniów). Można wybrać też „Nie pamiętam hasła”, które umożliwia reset.',
			'Po zalogowaniu bez profilu na bieżącej edycji system przekieruje Cię na uzupełnienie rejestracji nauczyciela.',
		],
		helpHref: '',
	},
	{
		version: '4.7.0',
		audience: 'teacher',
		title: 'Uczniowie w panelu',
		bullets: [
			'W `Dane konta` lista zadeklarowanych uczniów ma osobne pola imię i nazwisko — jak w formularzu rejestracji. Można samodzielnie dodawać uczniów (`Dodaj ucznia`) oraz usuwać (gdy jest więcej niż jeden zadeklarowany).',
		],
		helpHref: '',
	},
	{
		version: '4.7.0',
		audience: 'staff',
		title: 'Pomoc',
		bullets: [
			'Nowa pozycja menu `Pomoc` zawiera odpowiedzi na często zadawane pytania (treść i linki ustawia administrator w `Ustawienia`).',
		],
		helpHref: '/panel/pomoc',
	},
	{
		version: '4.7.0',
		audience: 'admin',
		title: 'Ustawienia',
		bullets: [
			'W `Ustawienia`: sekcja „Pomoc w panelu” — tekst wprowadzenia i linki dla nauczyciela, sekretarza i administratora.',
			'Komunikat przy odświeżaniu RSPO zawiera więcej szczegółów, gdy API MEN zwraca błąd.',
		],
		helpHref: '',
	},
	{
		version: '4.6.1',
		audience: 'teacher',
		title: 'Potwierdzanie konta',
		bullets: [
			'E-mail tuż po imieniu i nazwisku — z podpowiedzią o skrzynce do linku potwierdzającego.',
			'Bez potwierdzonego adresu e-mail przy logowaniu pokazuje się komunikat o nieaktywnym koncie.',
		],
		helpHref: '',
	},
	{
		version: '4.6.2',
		audience: 'admin',
		title: 'Odświeżenie szkół RSPO',
		bullets: [
			'W `Ustawienia`: przycisk „Przeglądaj i zatwierdź różnice RSPO” — tabela różnic względem poprzedniej edycji (mapowanie po RSPO), akcje dodaj/zastosuj/pomiń.',
			'Opcjonalny filtr powiatu (np. radzyński) ogranicza zakres porównania.',
		],
		helpHref: '',
	},
	{
		version: '4.6.1',
		audience: 'admin',
		title: 'Potwierdzenie e-mail i edycja',
		bullets: [
			'W `Nauczyciele` i `Uczniowie`: kolumna Email potwierdzony oraz ręczna zmiana w edycji rekordu.',
			'Przełącznik edycji w nagłówku panelu działa poprawnie (bez błędu serwera).',
		],
		helpHref: '',
	},
	{
		version: '4.6.0',
		audience: 'teacher',
		title: 'Rejestracja na nową edycję',
		bullets: [
			'Po zalogowaniu bez profilu na bieżącej edycji system przekieruje Cię na rejestrację nauczyciela.',
			'Dla nowej rejestracji hasło ustawiasz w formularzu; nowe konto wymaga potwierdzenia adresu email linkiem z wiadomości.',
			'Opiekun spoza szkoły zaznacza opcję pod polem `Kategoria` i wybiera okręg ręcznie.',
		],
		helpHref: '',
	},
	{
		version: '4.6.0',
		audience: 'admin',
		title: 'Start edycji — Mazowsze i szkoły',
		bullets: [
			'Nowa edycja dzieli woj. mazowieckie na Mazowsze I (kody 00/01/02) i Mazowsze II.',
			'W `Ustawienia` możesz pobrać raport różnic szkół względem RSPO (CSV) przed zatwierdzeniem zmian.',
		],
		helpHref: '',
	},
	{
		version: '4.5.0',
		audience: 'admin',
		title: 'Przełączanie edycji w panelu',
		bullets: [
			'W nagłówku panelu wybierasz edycję — listy, Excel i ustawienia dotyczą wybranej edycji.',
			'Rejestracja publiczna zawsze trafia do najnowszej edycji, niezależnie od wyboru w panelu.',
		],
		helpHref: '',
	},
	{
		version: '4.5.0',
		audience: 'secretary',
		title: 'Przełączanie edycji w panelu',
		bullets: [
			'W nagłówku panelu (lewy górny róg) można przełączać edycje. Po przełączeniu na wcześniejszą edycję (np. 2025/2026) dostępne są zeszłoroczne wyniki i raporty.',
		],
		helpHref: '',
	},
	{
		version: '4.4.2',
		audience: 'admin',
		title: 'Menu i statystyki po nowej edycji',
		bullets: [
			'W lewym menu dolne pozycje nie giną przy niższej wysokości okna — lista się przewija, odstępy się kurczą.',
			'Po `Nowa olimpiada` dostęp do ankiety uczniów startuje odznaczony.',
			'Statystyki ankiety i etapów dotyczą aktywnej edycji.',
		],
		helpHref: '',
	},
	{
		version: '4.4.1',
		audience: 'admin',
		title: 'Nowa edycja — jaśniejszy start',
		bullets: [
			'W `Ustawienia` widać numer aktywnej edycji; po `Nowa olimpiada` publikacje wyników startują odznaczone.',
			'Lista `Wysyłki` pokazuje tylko kampanie aktywnej edycji.',
			'Przy tworzeniu nowej edycji kopiowane są wszystkie okręgi i szkoły z poprzedniej edycji.',
		],
		helpHref: '',
	},
	{
		version: '4.4.0',
		audience: 'admin',
		title: 'Nowa edycja bez kasowania poprzedniej',
		bullets: [
			'W `Ustawienia` przycisk `Nowa olimpiada` tworzy kolejną edycję — dane poprzedniej edycji zostają w bazie.',
			'Listy uczniów, nauczycieli i etapów pokazują aktywną edycję; poprzednia edycja nie znika z bazy.',
			'Przy tworzeniu kopiowane są okręgi, szkoły i specjalizacje — rejestracja rusza na nowej edycji.',
		],
		helpHref: '',
	},
	{
		version: '4.3.0',
		audience: 'admin',
		title: 'Listy i Excel w panelu',
		bullets: [
			'W `Nauczyciele` wyszukiwanie obejmuje też afiliację.',
			'W eksporcie Excel nauczycieli długa lista uczniów zawija się w komórce.',
			'Na listach etapów I–IIIB widać kolumny `Okręg` i `Nauczyciel`.',
			'W Excelu wyników etapu III jest kolumna z tytułem naukowym nauczyciela.',
			'Ujednolicone etykiety punktów i sum na etapach IIA / IIB / IIIB.',
		],
		helpHref: '',
	},
	{
		version: '4.3.0',
		audience: 'admin',
		title: 'Rejestracja — co mówić szkołom',
		bullets: [
			'W formularzu nauczyciela przy `Uczniowie` jest adnotacja, że to nie zastępuje rejestracji ucznia; po wysłaniu pola nie edytuje się.',
			'Afiliacja nauczyciela: wybór szkoły z katalogu (z okręgiem) albo wpisanie uczelni ręcznie.',
			'Kategorie: `nauczyciel szkolny` / `opiekun naukowy` — bez „koordynatora szkolnego” i bez osobnych checkboxów roli.',
			'U ucznia: jeden e-mail kontaktowy; data urodzenia nie jest wymagana przy rejestracji (można uzupełnić później w panelu).',
			'Mała olimpiada: bez pola specjalizacji; ścieżka jak dla niepełnoletniego (bez przełącznika).',
		],
		helpHref: '',
	},
	{
		version: '4.3.0',
		audience: 'secretary',
		title: 'Listy etapów i uczniów',
		bullets: [
			'Na listach etapów I–IIIB widać `Okręg` i `Nauczyciel`.',
			'W Excelu etapu III jest tytuł naukowy nauczyciela.',
			'Przy zapisie ucznia data urodzenia nie blokuje już edycji — można uzupełnić później.',
			'Etykiety punktów na IIA / IIB / IIIB są zgodne z tym, co widać w tabelach.',
		],
		helpHref: '',
	},
	{
		version: '4.3.0',
		audience: 'secretary',
		title: 'Rejestracja — co mówić nauczycielom',
		bullets: [
			'Nauczyciel podczas rejestracji wskazuje uczniów przygotowywanych do zawodów (przy zmianie — edycja pola); konieczna jest też rejestracja samych uczestników.',
			'Pole `Afiliacja`: wybór szkoły z katalogu (system wybierze okręg) albo wpisanie uczelni ręcznie.',
			'W `Kategoria` dostępne są: nauczyciel pracujący w szkole oraz `Opiekun naukowy` — bez opcji „szkolny koordynator”.',
		],
		helpHref: '',
	},
	{
		version: '4.3.0',
		audience: 'secretary',
		title: 'Rejestracja — co mówić uczniom',
		bullets: [
			'Do rejestracji potrzebny jest jeden e-mail kontaktowy; data urodzenia nie jest wymagana przy rejestracji.',
			'Mała olimpiada: bez pola specjalizacji; ścieżka jak dla niepełnoletniego (bez przełącznika).',
		],
		helpHref: '',
	},
	{
		version: '4.3.0',
		audience: 'teacher',
		title: 'Rejestracja nauczyciela',
		bullets: ['Wpisanie ucznia przez nauczyciela nie zastępuje rejestracji ucznia.'],
		helpHref: '',
	},
];

export function filterWhatsNewForRole(entries: WhatsNewEntry[], role: WhatsNewRole): WhatsNewEntry[] {
	return entries.filter((e) => audienceMatches(e.audience, role));
}

/**
 * Wpisy nowsze niż lastSeen (wyłącznie), do bieżącej wersji włącznie.
 * Brak lastSeen → wszystkie wpisy ≤ current (istniejące konta przy pierwszym
 * wdrożeniu „Co nowego”). Nowo zarejestrowani dostają meta przy tworzeniu konta.
 */
export function unreadWhatsNewEntries(opts: {
	role: WhatsNewRole;
	currentVersion: string;
	lastSeenVersion: string | null | undefined;
	entries?: WhatsNewEntry[];
}): WhatsNewEntry[] {
	const all = filterWhatsNewForRole(opts.entries ?? WHATS_NEW_ENTRIES, opts.role).filter(
		(e) => compareSemver(e.version, opts.currentVersion) <= 0,
	);

	if (!opts.lastSeenVersion?.trim()) {
		return all;
	}
	return all.filter((e) => compareSemver(e.version, opts.lastSeenVersion!) > 0);
}

/** Pełna historia dla roli (strona /panel/co-nowego). */
export function allWhatsNewForRole(opts: {
	role: WhatsNewRole;
	currentVersion?: string;
	entries?: WhatsNewEntry[];
}): WhatsNewEntry[] {
	const current = opts.currentVersion ?? CURRENT_APP_VERSION;
	return filterWhatsNewForRole(opts.entries ?? WHATS_NEW_ENTRIES, opts.role)
		.filter((e) => compareSemver(e.version, current) <= 0)
		.sort((a, b) => compareSemver(b.version, a.version));
}

/** Grupuje kolejne wpisy tej samej wersji. */
export function groupWhatsNewByVersion(entries: WhatsNewEntry[]): { version: string; items: WhatsNewEntry[] }[] {
	const map = new Map<string, WhatsNewEntry[]>();
	for (const e of entries) {
		const list = map.get(e.version) ?? [];
		list.push(e);
		map.set(e.version, list);
	}
	return [...map.entries()].sort((a, b) => compareSemver(b[0], a[0])).map(([version, items]) => ({ version, items }));
}
